import { Draft } from 'immer';

import { finleyAssert } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { BaseUnusedFeeFormData } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/expense-mechanics/[expenseId]/UnusedFee';
import { tAccountToLabel } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/expense-mechanics/dataMappings';

type CreditAgreementFullArtifact = CreditAgreement.DomainEntity.CreditAgreementFullArtifact;

// This function receives a 'draft' of the Credit Agreement, which is provided by the immer library.
// This means that it is ok to mutate the `draft` variable - this produces a new CA object, and leaves
// the original CA reference untouched.
export const setFormValues = (
  draft: Draft<CreditAgreementFullArtifact>,
  formValues: BaseUnusedFeeFormData,
) => {
  if (!draft.liabilityAccounts) {
    draft.liabilityAccounts = [];
  }

  const expenses = draft?.liabilityAccounts?.filter(
    (la) =>
      la.counterpartySlug === formValues.capitalProviderSlug &&
      la.tAccount === formValues.tAccount,
  );

  finleyAssert(
    expenses.length <= 1,
    `Found ${expenses.length} matching expense(s) from [counterpartySlug: ${formValues.capitalProviderSlug}]. Was just expecting one or fewer matching expenses`,
  );

  const expense = expenses[0];

  // only update liability account if formValues have all required fields provided. capitalProviderSlug is the id of the expense. a fee without a value is not a fee. a fee must have a name set.
  if (
    !formValues.capitalProviderSlug ||
    formValues.value === null ||
    !formValues.name
  ) {
    return;
  }

  // if no expenses exist, add a new one
  if (expenses.length === 0) {
    const newLiabilityAccount: CreditAgreement.DomainEntity.LiabilityAccountProperty = {
      type: CreditAgreement.DomainEntity.PropertyType.LiabilityAccountProperty,
      _config_: { inputConfigurations: [] },
      _isWrittenIn_: true,
      counterpartySlug: formValues.capitalProviderSlug,
      name: formValues.name || tAccountToLabel[formValues.tAccount],
      tAccount: formValues.tAccount,
      value: String(formValues.value),
    };
    draft.liabilityAccounts.push(newLiabilityAccount);
    return;
  }

  if (expense) {
    expense.name = formValues.name;
    expense.counterpartySlug = formValues.capitalProviderSlug;
    expense.value = String(formValues.value);
    expense.tAccount = formValues.tAccount;
  }
};
